import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import find from 'lodash/find';

import MDVPlan from 'model/MDVPlan';
import MDVService from 'model/MDVService';
import MDVMetaService from 'model/MDVMetaService';
import {
  COST_TYPE,
  IN_NETWORK,
  NOT_COVERED_FORMATTED,
  NO_INFO,
  SERVICE_APPLICABLE,
  FREQUENCIES,
  AGE_BAND,
} from 'modules/benefitsWellness/constants/BenefitConstants';
import {
  DOUBLE_DASH,
  EMPTY_VALUE,
  NO_DATA,
  NOT_APPLICABLE,
  NOT_APPLICABLE_FORMATTED,
  TRIPLE_DASH,
} from 'utils/planRecommenderUtils/planRecommenderUtils';
import FrequencyLabels from 'modules/benefitsWellness/enums/FrequencyLabels';
import PolicyEnable from 'modules/benefitsWellness/enums/PolicyEnable';
import MDVMetaGroup from 'model/MDVMetaGroup';
import { baseApi } from 'utils/apiUtil';
import Plan from 'model/Plan';
import PlanReference from 'model/PlanReference';
import ContributionFrequency from 'model/ContributionFrequency';
import { trackEvents } from 'utils/initGA4';
import { addHttp } from 'utils/httpUtil';
import { ALL_DOCUMENT_TYPE } from 'modules/resources/constants';
import { MDVPlanSuperscript, Parameter } from 'model/LableTypes';
import { CARD_FREQUENCY_KEYS } from 'constants/languageConstants';
import { getText } from 'utils/stringUtil';
import MDVTypes from 'modules/benefitsWellness/enums/MDVTypes';
import { store } from 'app/store';

const defaultValue = '-';
const INDIVIDUAL_DEDUCTIBLE_IN = 'Individual Deductible';
const INDIVIDUAL_DEDUCTIBLE_OUT = 'Individual Deductible';
const FAMILY_DEDUCTIBLE_IN = 'Family Deductible';
const FAMILY_DEDUCTIBLE_OUT = 'Family Deductible';
const ANNUAL_MAXIMUM = 'Annual Maximum (Per Person)';
const ORTHODONTIC_MAXIMUM = 'Orthodontia Maximum';
const INDIVIDUAL_DEDUCTIBLE_IN_WITHIN_FAMILY = 'Deductible (Ind within Fam)';
const INDIVIDUAL_DEDUCTIBLE_OUT_WITHIN_FAMILY = 'Deductible (Ind within Fam)';
const INDIVIDUAL_OUT_OF_POCKET_MAX = 'Individual Out-of-Pocket Max';
const FAMILY_OUT_OF_POCKET_MAX = 'Family Out-of-Pocket Max';
const OUT_OF_POCKET_WITHIN_FAMILY = 'Out-of-Pocket Max (Ind within Fam)';
const RX_INDIVIDUAL_DEDUCTIBLE = 'Individual Deductible';
const RX_FAMILY_DEDUCTIBLE = 'Family Deductible';
const RX_INDIVIDUAL_OOP_MAX = 'Individual Out-of-Pocket Max';
const RX_FAMILY_OOP_MAX = 'Family Out-of-Pocket Max';
export const getLanguage = (): 'ENGLISH' | 'SPANISH' => {
  try {
    const state = store.getState() as any;
    const lang = state?.benguide?.data?.language;
    return lang === 'SPANISH' ? 'SPANISH' : 'ENGLISH';
  } catch (e) {
    return 'ENGLISH';
  }
};

export const changePlanOrder = (
  oldIndex: number,
  newIndex: number,
  plans: MDVPlan[],
  onUpdatePlanList: Function
) => {
  const clonedPlan = cloneDeep(plans) as any[];
  if (newIndex >= clonedPlan.length) {
    let k = newIndex - clonedPlan.length + 1;
    while (k--) {
      clonedPlan.push(undefined);
    }
  }
  clonedPlan.splice(newIndex, 0, clonedPlan.splice(oldIndex, 1)[0]);

  onUpdatePlanList(clonedPlan, false);
};

export const checkIfPlanDocumentsAvailable = (
  plans: MDVPlan[],
  documentType: string
) => {
  const availableDocuments = [] as string[];
  plans.forEach((plan: MDVPlan) => {
    if (plan.documents.hasOwnProperty(documentType)) {
      availableDocuments.push(plan.documents[documentType]);
    }
  });
  return !isEmpty(availableDocuments);
};

export const getConfiguredBenefitCodeList = (titleGroups: MDVMetaGroup[]) => {
  const benefitCodes = [] as number[];
  titleGroups.forEach((group) => {
    group.elements?.forEach((groupItem: any) => {
      if ('serviceBenefitCode' in groupItem) {
        benefitCodes.push(groupItem.serviceBenefitCode);
      }
    });
  });
  return benefitCodes;
};

export const getAdditionalServices = (
  planList: MDVPlan[],
  definedBenefitCodes: number[],
  selectedNetwork: string
): MDVMetaService[] => {
  const additionalServiceList: MDVMetaService[] = [];
  const alreadyIncludedCodes = new Set<number>();
  const networkKey =
    selectedNetwork === 'IN_NETWORK' ? 'inNetwork' : 'outOfNetwork';

  for (const plan of planList) {
    for (const service of plan.customServices ?? []) {
      const code = service.benefitCode?.code;
      if (
        code &&
        !definedBenefitCodes.includes(code) &&
        !alreadyIncludedCodes.has(code)
      ) {
        additionalServiceList.push({
          fieldName: 'additionalService',
          fieldValue: service.serviceValue?.[networkKey]?.info
            ? service.serviceValue?.[networkKey]?.info
            : service.serviceValue?.[networkKey]?.copay
            ? service.serviceValue?.[networkKey]?.copay
            : service.serviceValue?.[networkKey]?.coinsurance,
          serviceBenefitCode: code,
          fieldDescription: service.benefitCode.shortName,
          fieldFormatter: 'customServiceFormatter',
        });
        alreadyIncludedCodes.add(code);
      }
    }
  }

  return additionalServiceList;
};

const appendSuperScripts = (data: MDVService, formattedValue: string) => {
  if (data && data.costSharingPolicy) {
    if (
      (data.costSharingPolicy.toString().toLowerCase() === COST_TYPE.COPAY &&
        (data.copayPriorToDeductible === PolicyEnable.NO ||
          data.copayPriorToDeductible === undefined)) ||
      (data.costSharingPolicy.toString().toLowerCase() ===
        COST_TYPE.COINSURANCE &&
        (data.copayPriorToDeductible === PolicyEnable.NO ||
          data.copayPriorToDeductible === undefined)) ||
      (data.costSharingPolicy.toString().toLowerCase() === COST_TYPE.OTHER &&
        !isEmpty(data.info) &&
        (data.copayPriorToDeductible === PolicyEnable.NO ||
          data.copayPriorToDeductible === undefined))
    ) {
      return `${formattedValue} SUP-1`;
    }
  }
  return formattedValue;
};

const showFrequency = (
  frequency: FrequencyLabels,
  language: 'ENGLISH' | 'SPANISH' = 'ENGLISH'
) => {
  const key =
    CARD_FREQUENCY_KEYS?.[frequency as keyof typeof CARD_FREQUENCY_KEYS];
  if (!key) return '';
  return getText(key as string, language) || '';
};

const getFormattedCopayValue = (data: MDVService, includeFrequency = true) =>
  COST_TYPE.COPAY in data
    ? `${currencyFormatter(parseFloat(data.copay || ''))} ${
        includeFrequency && data.copayFrequency
          ? showFrequency(data.copayFrequency as FrequencyLabels, getLanguage())
          : ''
      }`
    : defaultValue;

const getFormattedCoinsuranceValue = (data: MDVService) =>
  COST_TYPE.COINSURANCE in data ? `${data.coinsurance}%` : defaultValue;

const regularFormatter = (data: MDVService, includeFrequency = true) => {
  if (data && data.benefitCovered) {
    if (data.costSharingPolicy?.toString() === NOT_APPLICABLE)
      return NOT_APPLICABLE_FORMATTED;
    if (data.benefitCovered.toString().toLowerCase() === 'no')
      return NOT_COVERED_FORMATTED;
    if (data.benefitCovered.toString().toLowerCase() === 'no_info')
      return NO_INFO;
  }
  if (data && data.costSharingPolicy) {
    if (data.costSharingPolicy.toString().toLowerCase() === COST_TYPE.COPAY)
      return appendSuperScripts(
        data,
        getFormattedCopayValue(data, includeFrequency)
      );
    if (
      data.costSharingPolicy.toString().toLowerCase() === COST_TYPE.COINSURANCE
    )
      return appendSuperScripts(data, getFormattedCoinsuranceValue(data));
    if (data.costSharingPolicy.toString().toLowerCase() === COST_TYPE.OTHER)
      return appendSuperScripts(data, data.info || '') || defaultValue;
  }
  if (data && !isEmpty(data.info)) {
    return appendSuperScripts(data, data.info || '') || defaultValue;
  }
  return defaultValue;
};

export const currencyFormatter = (num: number) => {
  let value =
    num && !isNaN(num)
      ? '$' +
        Number(num)
          .toFixed(2)
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
      : '$0';

  const decimalsOfNum = value.split('.');
  if (decimalsOfNum[1] === '00') value = decimalsOfNum[0];

  return value;
};

export const coverageFormatter = (
  value: number | undefined,
  applicableText: string
) => {
  if (applicableText !== undefined) {
    if (applicableText === SERVICE_APPLICABLE.NOT_APPLICABLE) {
      return NOT_APPLICABLE_FORMATTED;
    } else if (
      applicableText === SERVICE_APPLICABLE.COVERED &&
      value !== undefined
    ) {
      return String(value);
    } else if (applicableText === SERVICE_APPLICABLE.NOT_COVERED) {
      return NOT_COVERED_FORMATTED;
    }
    return defaultValue;
  }
  return defaultValue;
};

export const fieldFormatters = (data: MDVService, formatter: string) => {
  if (formatter === 'customServiceFormatter') return regularFormatter(data);
};

export const appendRxSuperScripts = (rxObject: any, currentNetwork: string) => {
  let response = '';
  if (rxObject.inNetwork && currentNetwork === 'inNetwork') {
    const rxMap = new Map(Object.entries(rxObject.inNetwork));
    rxMap.forEach((value: any) => {
      if (
        value.copayPriorToDeductible === 'NO' ||
        (value.costSharingPolicy !== undefined &&
          value.copayPriorToDeductible === undefined)
      ) {
        response = 'SUP-1';
      }
    });
  } else if (rxObject.outOfNetwork && currentNetwork === 'outOfNetwork') {
    const rxMap = new Map(Object.entries(rxObject.outOfNetwork));
    rxMap.forEach((value: any) => {
      if (
        value.copayPriorToDeductible === 'NO' ||
        (value.costSharingPolicy !== undefined &&
          value.copayPriorToDeductible === undefined)
      ) {
        response = 'SUP-1';
      }
    });
  }

  return response;
};

export const hasSuperScripts = (plans: MDVPlan[], network: string) => {
  const superScriptSet = new Set();
  const currentNetwork =
    network === IN_NETWORK.value ? 'inNetwork' : 'outOfNetwork';

  for (const plan of plans) {
    if (plan.customServices) {
      for (const service of plan.customServices) {
        const val = appendSuperScripts(
          service.serviceValue[currentNetwork],
          ''
        );

        if (val.length > 0) superScriptSet.add(val.toString().trim());
      }
    }
    if (plan.rxCosts) {
      const val = appendRxSuperScripts(plan.rxCosts, currentNetwork);
      if (val.length > 0) superScriptSet.add(val);
    }
    if (plan.mailOrderRxCosts) {
      const val = appendRxSuperScripts(plan.mailOrderRxCosts, currentNetwork);
      if (val.length > 0) superScriptSet.add(val);
    }
  }

  return superScriptSet;
};

const getRxValues = (
  object: any = {},
  network: 'IN' | 'OUT',
  type: 'MAIL_ORDER' | 'RETAIL'
) => {
  if (!object) {
    return {};
  } else {
    const cost = new Map();
    const keys = Object?.keys(object) || [];
    keys?.forEach((key) => {
      cost.set(
        `MEDICAL_RX_${key?.replace('_', '')}_${type}_${network}`,
        object?.[key]?.info
      );
    });

    return Object.fromEntries(cost);
  }
};

const getServiceValues = (
  customServices: any[] = [],
  network: 'IN' | 'OUT',
  benefitCategory: 'MEDICAL' | 'DENTAL',
  parameters: Parameter[] = []
) => {
  const networkProperty = network === 'IN' ? 'inNetwork' : 'outOfNetwork';
  if (isEmpty(customServices)) {
    return {};
  } else {
    const cost = new Map();
    customServices?.forEach((service) => {
      let fieldName = `${benefitCategory}_${service?.benefitCode?.shortName
        ?.trim()
        .toUpperCase()
        .replace(/ /g, '_')}_${network}`;
      // map the field name to the key in the parameters
      parameters?.forEach((param: any) => {
        if (
          param?.shortName === service?.benefitCode?.shortName?.trim() &&
          param?.network === network
        ) {
          fieldName = param?.key;
        }
      });
      cost.set(fieldName, service?.serviceValue?.[networkProperty]?.info);
    });

    return Object.fromEntries(cost);
  }
};

export const getListedMultiplePlanSuperScripts = (
  plans: MDVPlan[],
  benefitCategory: 'MEDICAL' | 'DENTAL'
): MDVPlanSuperscript[] => {
  if (!plans?.length) return [];

  const data: any[] = [];

  const getDeductibleData = (plan: MDVPlan, category: 'MEDICAL' | 'DENTAL') => {
    if (category === 'MEDICAL') {
      return {
        MEDICAL_FAMILY_DEDUCTIBLE_IN: plan?.deductibles?.familyInNetworkCost,
        MEDICAL_FAMILY_DEDUCTIBLE_OUT:
          plan?.deductibles?.familyOutOfNetworkCost,
        MEDICAL_INDIVIDUAL_DEDUCTIBLE_IN:
          plan?.deductibles?.individualInNetworkCost,
        MEDICAL_INDIVIDUAL_DEDUCTIBLE_OUT:
          plan?.deductibles?.individualOutOfNetworkCost,
        MEDICAL_INDIVIDUAL_WITHIN_FAMILY_DEDUCTIBLE_IN:
          plan?.deductibles?.individualWithinFamilyInNetworkCost,
        MEDICAL_INDIVIDUAL_WITHIN_FAMILY_DEDUCTIBLE_OUT:
          plan?.deductibles?.individualWithinFamilyOutOfNetworkCost,
        MEDICAL_INDIVIDUAL_OOP_MAX_IN:
          plan?.outOfPocket?.individualInNetworkCost,
        MEDICAL_INDIVIDUAL_OOP_MAX_OUT:
          plan?.outOfPocket?.individualOutOfNetworkCost,
        MEDICAL_FAMILY_OOP_MAX_IN: plan?.outOfPocket?.familyInNetworkCost,
        MEDICAL_FAMILY_OOP_MAX_OUT: plan?.outOfPocket?.familyOutOfNetworkCost,
        MEDICAL_INDIVIDUAL_WITHIN_FAMILY_OOP_MAX_IN:
          plan?.outOfPocket?.individualWithinFamilyInNetworkCost,
        MEDICAL_INDIVIDUAL_WITHIN_FAMILY_OOP_MAX_OUT:
          plan?.outOfPocket?.individualWithinFamilyOutOfNetworkCost,
      };
    } else {
      return {
        DENTAL_INDIVIDUAL_DEDUCTIBLE_IN:
          plan?.deductibles?.individualInNetworkCost,
        DENTAL_INDIVIDUAL_DEDUCTIBLE_OUT:
          plan?.deductibles?.individualOutOfNetworkCost,
        DENTAL_FAMILY_DEDUCTIBLE_IN: plan?.deductibles?.familyInNetworkCost,
        DENTAL_FAMILY_DEDUCTIBLE_OUT: plan?.deductibles?.familyOutOfNetworkCost,
        DENTAL_PLAN_YEAR_MAX_IN: plan?.calendarYearMax?.inNetworkCost,
        DENTAL_PLAN_YEAR_MAX_OUT: plan?.calendarYearMax?.outOfNetworkCost,
        DENTAL_ORTHODONTIA_MAX_IN: plan?.orthodontiaLifetimeMax?.inNetworkCost,
        DENTAL_ORTHODONTIA_MAX_OUT:
          plan?.orthodontiaLifetimeMax?.outOfNetworkCost,
      };
    }
  };

  const getRxData = (plan: MDVPlan) => ({
    MEDICAL_RX_INDIVIDUAL_DEDUCTIBLE_IN:
      plan?.rxDeductiblesAndOop?.individualDeductibleInNetworkCost,
    MEDICAL_RX_INDIVIDUAL_DEDUCTIBLE_OUT:
      plan?.rxDeductiblesAndOop?.individualDeductibleOutOfNetworkCost,
    MEDICAL_RX_FAMILY_DEDUCTIBLE_IN:
      plan?.rxDeductiblesAndOop?.familyDeductibleInNetworkCost,
    MEDICAL_RX_FAMILY_DEDUCTIBLE_OUT:
      plan?.rxDeductiblesAndOop?.familyDeductibleOutOfNetworkCost,
    MEDICAL_RX_INDIVIDUAL_OOP_MAX_IN:
      plan?.rxDeductiblesAndOop?.individualOopMaxInNetworkCost,
    MEDICAL_RX_INDIVIDUAL_OOP_MAX_OUT:
      plan?.rxDeductiblesAndOop?.individualOopMaxOutOfNetworkCost,
    MEDICAL_RX_FAMILY_OOP_MAX_IN:
      plan?.rxDeductiblesAndOop?.familyOopMaxInNetworkCost,
    MEDICAL_RX_FAMILY_OOP_MAX_OUT:
      plan?.rxDeductiblesAndOop?.familyOopMaxOutOfNetworkCost,
    ...getRxValues(plan?.rxCosts?.inNetwork, 'IN', 'RETAIL'),
    ...getRxValues(plan?.rxCosts?.outOfNetwork, 'OUT', 'RETAIL'),
    ...getRxValues(plan?.mailOrderRxCosts?.inNetwork, 'IN', 'MAIL_ORDER'),
    ...getRxValues(plan?.mailOrderRxCosts?.outOfNetwork, 'OUT', 'MAIL_ORDER'),
  });

  const getServiceData = (plan: MDVPlan, category: 'MEDICAL' | 'DENTAL') => ({
    ...getServiceValues(
      plan?.customServices,
      'IN',
      category,
      plan?.planLabels?.parameters
    ),
    ...getServiceValues(
      plan?.customServices,
      'OUT',
      category,
      plan?.planLabels?.parameters
    ),
  });

  plans.forEach((plan) => {
    const deductibleData = getDeductibleData(plan, benefitCategory);
    const rxData = getRxData(plan);
    const serviceData = getServiceData(plan, benefitCategory);

    data.push({ ...deductibleData, ...rxData, ...serviceData });
  });

  const filterObjects = (firstArray: any[], secondArray: any[]) => {
    const validEntries: any[] = [];
    firstArray?.forEach((obj) => {
      Object.keys(obj)?.forEach((key) => {
        if (obj?.[key]) {
          // Use filter to find all matches in secondArray
          const matches = secondArray?.flat()?.filter((o) => o?.key === key);
          if (matches.length) {
            validEntries.push(...matches);
          }
        }
      });
    });

    return validEntries;
  };

  const parameters = plans?.map((plan) => plan?.planLabels?.parameters);
  const valueAvailable = filterObjects(data, parameters);

  const uniqueLabels = (items: any[]) => {
    const labelMap = new Map();
    items?.forEach((item) =>
      item?.labelVOs?.forEach((label: any) => {
        labelMap.set(label?.labelId, label);
      })
    );
    return Array.from(labelMap.values());
  };

  const uniqueLabelsArray = uniqueLabels(valueAvailable);

  return uniqueLabelsArray?.sort(
    (a, b) => Number(a?.superscript) - Number(b?.superscript)
  );
};

export const openWebLink = (weblink: string, benefitKind: string) => {
  window.open(addHttp(weblink), '_blank')?.focus();

  trackEvents({
    category: 'View Details',
    action: `view_${benefitKind}_details`,
    label: 'View Holiday and Time Off Details',
  });
};
export const openDocument = (
  id: string,
  reference: string,
  benefitKind: string
) => {
  const documentType = ALL_DOCUMENT_TYPE.includes(reference)
    ? reference
    : 'PLAN_ADDITIONAL_DOCUMENT';
  window
    .open(
      `${baseApi}/v1/plans/${id}/documents?documentType=${documentType}&benefitKind=${benefitKind}&isOpen=true&planDocumentName=${encodeURIComponent(
        reference
      )}`,
      '_blank'
    )
    ?.focus();

  trackEvents({
    category: 'View Details',
    action: `view_${benefitKind}_details`,
    label: 'View Holiday and Time Off Details',
  });
};

export const viewPlanSummary = (
  id: string,
  planDocument: string,
  benefitKind: string
) => {
  window
    .open(
      `${baseApi}/v1/plans/${id}/documents?documentType=${planDocument}&benefitKind=${benefitKind}&isOpen=true`,
      '_blank'
    )
    ?.focus();

  trackEvents({
    category: 'View Details',
    action: `view_${benefitKind}_details`,
    label: `View ${benefitKind} Details`,
  });
};

export const getDocumentDownloadURL = (
  id: string,
  planDocument: string,
  benefitKind: string
) => {
  return `${baseApi}/v1/plans/${id}/documents?documentType=${planDocument}&benefitKind=${benefitKind}`;
};

export const buildPlanRefsFromPlan = (
  plan: Plan,
  benefitCategory: string
): PlanReference => {
  return {
    id: plan.id,
    planId: plan.id,
    planName: plan.name,
    name: plan.name,
    isArchived: plan.archived,
    startDate: plan.startDate,
    endDate: plan.endDate,
    archived: plan.archived,
    revision: plan.revision,
    groups: plan.groups,
    benefitKind: plan.benefitKind,
    benefitCategory: benefitCategory,
    orderNo: plan.orderNo,
    carrier: {
      id: get(plan, 'carrier.id', null),
      name: get(plan, 'carrier.name', null),
    },
    benefitCarrier: {
      id: get(plan, 'benefitCarrier.id', null),
      name: get(plan, 'benefitCarrier.name', null),
    },
  };
};

export const findFrequencyByBenefitKind = (
  frequencyContributions: ContributionFrequency[],
  benefitKind: string
) => {
  return (
    find(frequencyContributions, { benefitKind: benefitKind }) ||
    ({} as ContributionFrequency)
  );
};

export const getEEContributionByFrequency = (
  frequency: string,
  contribution: any
) => {
  if (frequency === FREQUENCIES.MONTHLY.value) {
    return contribution.employeeCost;
  } else if (frequency === FREQUENCIES.BI_WEEKLY.value) {
    return contribution.employeeBiWeeklyCost;
  } else if (frequency === FREQUENCIES.WEEKLY.value) {
    return contribution.employeeWeeklyCost;
  } else {
    return contribution.employeeSemiMonthlyCost;
  }
};

export const getAgeBandedPlans = (plans: any) => {
  return (
    (!isEmpty(plans) &&
      plans.filter((plan: MDVPlan) =>
        Object.values(plan?.rates || {}).every(
          (rate: any) => rate?.type === AGE_BAND
        )
      )) ||
    []
  );
};

export const getEEContributionField = (frequency: string) => {
  if (frequency === FREQUENCIES.MONTHLY.value) {
    return 'employeeCost';
  } else if (frequency === FREQUENCIES.BI_WEEKLY.value) {
    return 'employeeBiWeeklyCost';
  } else if (frequency === FREQUENCIES.WEEKLY.value) {
    return 'employeeWeeklyCost';
  } else {
    return 'employeeSemiMonthlyCost';
  }
};

export const isRxDeductiblesAvailable = (addedPlanList: MDVPlan[]) => {
  const availability = { inNetwork: false, outNetwork: false };
  addedPlanList?.map((plan: any) => {
    if (
      !['', 'null', null, DOUBLE_DASH, TRIPLE_DASH, NO_DATA].includes(
        plan?.rxDeductiblesAndOop?.individualDeductibleInNetworkCost
      ) ||
      !['', 'null', null, DOUBLE_DASH, TRIPLE_DASH, NO_DATA].includes(
        plan?.rxDeductiblesAndOop?.familyDeductibleInNetworkCost
      )
    ) {
      availability.inNetwork = true;
    }

    if (
      !['', 'null', null, DOUBLE_DASH, TRIPLE_DASH, NO_DATA].includes(
        plan?.rxDeductiblesAndOop?.individualDeductibleOutOfNetworkCost
      ) ||
      !['', 'null', null, DOUBLE_DASH, TRIPLE_DASH, NO_DATA].includes(
        plan?.rxDeductiblesAndOop?.familyDeductibleOutOfNetworkCost
      )
    ) {
      availability.outNetwork = true;
    }
  });

  return availability;
};

export const isRxOopAvailable = (addedPlanList: MDVPlan[]) => {
  const availability = { inNetwork: false, outNetwork: false };
  addedPlanList?.map((plan) => {
    if (
      !['', 'null', null].includes(
        plan?.rxDeductiblesAndOop?.individualOopMaxInNetworkCost
      ) ||
      !['', 'null', null].includes(
        plan?.rxDeductiblesAndOop?.familyOopMaxInNetworkCost
      )
    ) {
      availability.inNetwork = true;
    }

    if (
      !['', 'null', null, undefined].includes(
        plan?.rxDeductiblesAndOop?.individualOopMaxOutOfNetworkCost
      ) ||
      !['', 'null', null, undefined].includes(
        plan?.rxDeductiblesAndOop?.familyOopMaxOutOfNetworkCost
      ) ||
      !['', 'null', null, undefined].includes(
        plan?.rxDeductiblesAndOop?.individualOopMaxOutOfNetworkApplicable
      ) ||
      !['', 'null', null, undefined].includes(
        plan?.rxDeductiblesAndOop?.familyOopMaxOutOfNetworkApplicable
      )
    ) {
      availability.outNetwork = true;
    }
  });

  return availability;
};

export const showRxRow = (
  title: any,
  addedPlanList: MDVPlan[],
  selectedNetwork: string
) => {
  return (
    (title.fieldName !== 'rxDeductibles' ||
      isRxDeductiblesAvailable(addedPlanList).inNetwork ||
      selectedNetwork !== 'IN_NETWORK') &&
    (title.fieldName !== 'rxDeductibles' ||
      isRxDeductiblesAvailable(addedPlanList).outNetwork ||
      selectedNetwork !== 'OUT_NETWORK') &&
    (title.fieldName !== 'rxOOP' ||
      isRxOopAvailable(addedPlanList).inNetwork ||
      selectedNetwork !== 'IN_NETWORK') &&
    (title.fieldName !== 'rxOOP' ||
      isRxOopAvailable(addedPlanList).outNetwork ||
      selectedNetwork !== 'OUT_NETWORK')
  );
};

export const showRxRowTitle = (title: any) => {
  if (title.fieldName === 'rxDeductibles' || title.fieldName === 'rxOOP') {
    return true;
  }
  return false;
};

const isTierDataInvalid = (tierData: {
  info?: string | null;
  copay?: string | number | null;
  coinsurance?: string | number | null;
}): boolean => {
  if (!isInvalidValue(tierData?.info)) return false;
  if (!isInvalidValue(tierData?.copay)) return false;
  if (!isInvalidValue(tierData?.coinsurance)) return false;
  return true;
};

const isInvalidValue = (value: string | number | undefined | null): boolean => {
  if (value === null || value === undefined) return true;

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    const isInvalidValue =
      trimmedValue === '' ||
      trimmedValue === '-' ||
      trimmedValue === '--' ||
      trimmedValue === '---' ||
      trimmedValue.toLowerCase() === 'no data';
    return isInvalidValue;
  }
  if (typeof value === 'number') {
    return false;
  }
  return true;
};

export const getNotApplicableTitles = (
  planList: MDVPlan[],
  planType: MDVTypes,
  networkType: 'IN' | 'OUT'
) => {
  const notApplicableTitles: string[] = [];
  const allPlansMissingNetwork = planList.every(
    (plan) =>
      !('planNetworkName' in plan) ||
      !plan.planNetworkName ||
      isInvalidValue(plan.planNetworkName)
  );
  if (allPlansMissingNetwork) {
    notApplicableTitles.push('UnavailablePlanNetwork');
  }
  if (planType === MDVTypes.MEDICAL) {
    const titleFieldMap = {
      deductibles: {
        in: {
          individualInNetworkCost: INDIVIDUAL_DEDUCTIBLE_IN,
          familyInNetworkCost: FAMILY_DEDUCTIBLE_IN,
          individualWithinFamilyInNetworkCost:
            INDIVIDUAL_DEDUCTIBLE_IN_WITHIN_FAMILY,
        },
        out: {
          individualOutOfNetworkCost: INDIVIDUAL_DEDUCTIBLE_OUT,
          familyOutOfNetworkCost: FAMILY_DEDUCTIBLE_OUT,
          individualWithinFamilyOutOfNetworkCost:
            INDIVIDUAL_DEDUCTIBLE_OUT_WITHIN_FAMILY,
        },
      },
      outOfPocket: {
        in: {
          individualInNetworkCost: INDIVIDUAL_OUT_OF_POCKET_MAX,
          familyInNetworkCost: FAMILY_OUT_OF_POCKET_MAX,
          individualWithinFamilyInNetworkCost: OUT_OF_POCKET_WITHIN_FAMILY,
        },
        out: {
          individualOutOfNetworkCost: INDIVIDUAL_OUT_OF_POCKET_MAX,
          familyOutOfNetworkCost: FAMILY_OUT_OF_POCKET_MAX,
          individualWithinFamilyOutOfNetworkCost: OUT_OF_POCKET_WITHIN_FAMILY,
        },
      },
    };

    for (const category of ['deductibles', 'outOfPocket'] as const) {
      const applicableMap =
        titleFieldMap[category][networkType.toLowerCase() as 'in' | 'out'];

      for (const [field, title] of Object.entries(applicableMap)) {
        const allCovered = planList.every((plan) => {
          const value = plan?.[category]?.[field];
          return isInvalidValue(value);
        });

        if (allCovered && !notApplicableTitles.includes(title)) {
          notApplicableTitles.push('MEDICAL_DEDUCTIBLE_' + title);
        }
      }
    }

    const allFieldNames = new Set<string>();
    for (const plan of planList) {
      plan.customFields?.forEach((field) => allFieldNames.add(field.fieldName));
    }

    for (const fieldName of Array.from(allFieldNames)) {
      const allEmpty = planList.every((plan) =>
        plan.customFields?.some(
          (f) => f.fieldName === fieldName && isInvalidValue(f.fieldValue)
        )
      );
      if (allEmpty && !notApplicableTitles.includes(fieldName)) {
        notApplicableTitles.push(fieldName);
      }
    }

    const serviceCodeToTitleMap = new Map<number, string>();

    const targetServices: Record<number, string> = {
      1: 'Office Visit',
      2: 'Specialist Visit',
      13: 'Urgent Care',
      15: 'Emergency Room',
      34: 'Chiropractic Care',
      37: 'Complex Imaging',
      40: 'Acupuncture Care',
      77: 'Diagnostic Lab/ X-Ray',
      87: 'Hospital Inpatient',
      88: 'Hospital Outpatient',
      95: 'Preventive Care',
      107: 'Coinsurance',
      108: 'Virtual Care',
    };

    planList.forEach((plan) => {
      (plan.customServices || []).forEach((service) => {
        const info =
          service.serviceValue?.[
            networkType === 'IN' ? 'inNetwork' : 'outOfNetwork'
          ]?.info;
        if (
          service.benefitCode?.code !== undefined &&
          service.benefitCode?.benefitKind === planType.toUpperCase() &&
          service.benefitCode.shortName &&
          info !== ''
        ) {
          serviceCodeToTitleMap.set(
            service.benefitCode.code,
            service.benefitCode.shortName
          );
        }
      });
    });

    // We need to have a unified map of service codes to titles considering the services in the plan as well as the default services defined in target services
    const unifiedMap = new Map<number, string>();

    serviceCodeToTitleMap.forEach((value, key) => {
      unifiedMap.set(key, value);
    });

    Object.entries(targetServices).forEach(([key, value]) => {
      const numKey = Number(key);
      if (!unifiedMap.has(numKey)) {
        unifiedMap.set(numKey, value);
      }
    });

    Array.from(unifiedMap).forEach(([code, title]) => {
      const allPlansNotCovered = planList.every((plan) => {
        const allServices = [...(plan.customServices ?? [])];

        const matchingService = allServices.find(
          (service) => service.benefitCode?.code === code
        );

        if (!matchingService) return true;

        const infoField = getServiceValue(matchingService, networkType);
        return !infoField || isInvalidValue(infoField);
      });

      if (allPlansNotCovered && !notApplicableTitles.includes(title)) {
        notApplicableTitles.push('MEDICAL_SERVICES_' + title);
      }
    });
    // rxDeducitbles
    const EXPECTED_TIERS = Array.from({ length: 8 }, (_, i) => `TIER_${i + 1}`);

    for (const tier of EXPECTED_TIERS) {
      const allPlansMissingInfo =
        planList.length === 0 ||
        planList.every((plan) => {
          const rxNetwork = (
            networkType === 'IN'
              ? plan.rxCosts?.inNetwork
              : plan.rxCosts?.outOfNetwork
          ) as
            | Record<
                string,
                { info?: string; copay?: string; coinsurance?: string }
              >
            | undefined;

          const tierData = rxNetwork?.[tier];
          return !tierData || isTierDataInvalid(tierData);
        });

      const label = tier.replace('TIER_', 'Retail Tier ');
      const finalTitle = 'MEDICAL_RX_' + label;

      if (allPlansMissingInfo && !notApplicableTitles.includes(finalTitle)) {
        notApplicableTitles.push(finalTitle);
      }
    }

    const rxFieldMap = {
      in: {
        individualDeductibleInNetworkCost: RX_INDIVIDUAL_DEDUCTIBLE,
        familyDeductibleInNetworkCost: RX_FAMILY_DEDUCTIBLE,
        individualOopMaxInNetworkCost: RX_INDIVIDUAL_OOP_MAX,
        familyOopMaxInNetworkCost: RX_FAMILY_OOP_MAX,
      },
      out: {
        individualDeductibleOutOfNetworkCost: RX_INDIVIDUAL_DEDUCTIBLE,
        familyDeductibleOutOfNetworkCost: RX_FAMILY_DEDUCTIBLE,
        individualOopMaxOutOfNetworkCost: RX_INDIVIDUAL_OOP_MAX,
        familyOopMaxOutOfNetworkCost: RX_FAMILY_OOP_MAX,
      },
    };

    const applicableMap = rxFieldMap[networkType.toLowerCase() as 'in' | 'out'];

    if (applicableMap) {
      for (const [fieldKey, title] of Object.entries(applicableMap)) {
        const allNotApplicable = planList.every((plan) => {
          const value = plan?.rxDeductiblesAndOop?.[fieldKey];
          return isInvalidValue(value);
        });

        if (allNotApplicable && !notApplicableTitles.includes(title)) {
          notApplicableTitles.push('MEDICAL_RX_' + title);
        }
      }
    }

    const EXPECTED_MAIL_ORDER_TIERS = Array.from(
      { length: 8 },
      (_, i) => `TIER_${i + 1}`
    );

    // Mail Order Rx Logic – Insert into existing notApplicableTitles
    for (const tier of EXPECTED_MAIL_ORDER_TIERS) {
      const allPlansMissingInfo =
        planList.length === 0 ||
        planList.every((plan) => {
          const rxNetwork = (
            networkType === 'IN'
              ? plan.mailOrderRxCosts?.inNetwork
              : plan.mailOrderRxCosts?.outOfNetwork
          ) as
            | Record<
                string,
                {
                  info?: string;
                  benefitCovered?: string;
                  costSharingPolicy?: string;
                  copay?: string;
                }
              >
            | undefined;

          const tierData = rxNetwork?.[tier];
          return !tierData || isTierDataInvalid(tierData);
        });

      const label = tier.replace('TIER_', 'Mail Order Tier ');
      const finalTitle = 'MEDICAL_RX_' + label;

      if (allPlansMissingInfo && !notApplicableTitles.includes(finalTitle)) {
        notApplicableTitles.push(finalTitle);
      }
    }
  }
  if (planType === MDVTypes.DENTAL) {
    const calendarMaxFieldMap = {
      in: {
        inNetworkCost: ANNUAL_MAXIMUM,
      },
      out: {
        outOfNetworkCost: ANNUAL_MAXIMUM,
      },
    };

    const orthodonticFieldMap = {
      in: {
        inNetworkCost: ORTHODONTIC_MAXIMUM,
      },
      out: {
        outOfNetworkCost: ORTHODONTIC_MAXIMUM,
      },
    };

    const calendarFieldMap =
      calendarMaxFieldMap[networkType.toLowerCase() as 'in' | 'out'];
    const orthoFieldMap =
      orthodonticFieldMap[networkType.toLowerCase() as 'in' | 'out'];

    if (calendarFieldMap) {
      for (const [field, title] of Object.entries(calendarFieldMap)) {
        const allCovered = planList.every((plan) =>
          isInvalidValue(plan?.calendarYearMax?.[field])
        );

        if (allCovered && !notApplicableTitles.includes(title)) {
          notApplicableTitles.push('DENTAL_DEDUCTIBLE_' + title);
        }
      }
    }

    if (orthoFieldMap) {
      for (const [field, title] of Object.entries(orthoFieldMap)) {
        const allCovered = planList.every((plan) =>
          isInvalidValue(plan?.orthodontiaLifetimeMax?.[field])
        );

        if (allCovered && !notApplicableTitles.includes(title)) {
          notApplicableTitles.push('DENTAL_DEDUCTIBLE_' + title);
        }
      }
    }

    const titleFieldMap = {
      deductibles: {
        in: {
          individualInNetworkCost: INDIVIDUAL_DEDUCTIBLE_IN,
          familyInNetworkCost: FAMILY_DEDUCTIBLE_IN,
        },
        out: {
          individualOutOfNetworkCost: INDIVIDUAL_DEDUCTIBLE_OUT,
          familyOutOfNetworkCost: FAMILY_DEDUCTIBLE_OUT,
        },
      },
    };

    for (const category of ['deductibles'] as const) {
      const applicableMap =
        titleFieldMap[category][networkType.toLowerCase() as 'in' | 'out'];

      for (const [field, title] of Object.entries(applicableMap)) {
        const allCovered = planList.every((plan) =>
          isInvalidValue(plan?.[category]?.[field])
        );

        if (allCovered && !notApplicableTitles.includes(title)) {
          notApplicableTitles.push('DENTAL_DEDUCTIBLE_' + title);
        }
      }
    }

    const serviceCodeToTitleMap = new Map<number, string>();
    const targetServices: Record<number, string> = {
      38: 'Preventive Care',
      53: 'Basic Care',
      55: 'Major Care',
      97: 'Out-of-Network Reimbursement',
      99: 'Endodontic Care',
      100: 'Periodontic Care',
      98: 'Waiting Period',
      101: 'Implants',
    };

    planList.forEach((plan) => {
      (plan.customServices || []).forEach((service) => {
        if (
          service.benefitCode?.code !== undefined &&
          service.benefitCode?.benefitKind === planType.toUpperCase() &&
          service.benefitCode.shortName
        ) {
          serviceCodeToTitleMap.set(
            service.benefitCode.code,
            service.benefitCode.shortName
          );
        }
      });
    });

    const unifiedMap = new Map<number, string>();

    serviceCodeToTitleMap.forEach((value, key) => {
      unifiedMap.set(key, value);
    });

    Object.entries(targetServices).forEach(([key, value]) => {
      const numKey = Number(key);
      if (!unifiedMap.has(numKey)) {
        unifiedMap.set(numKey, value);
      }
    });

    // removing OON reimbursement for IN network
    Array.from(unifiedMap).forEach(([code, title]) => {
      if (code === 97 && networkType === 'IN') {
        return;
      }

      const allPlansNotCovered = planList.every((plan) => {
        const allServices = [...(plan.customServices ?? [])];

        const matchingService = allServices.find(
          (service) => service.benefitCode?.code === code
        );

        if (!matchingService) return true;

        const infoField = getServiceValue(matchingService, networkType);

        return !infoField || isInvalidValue(infoField);
      });

      if (allPlansNotCovered && !notApplicableTitles.includes(title)) {
        notApplicableTitles.push('DENTAL_SERVICES_' + title);
      }
    });

    const allFieldNames = new Set<string>();
    for (const plan of planList) {
      plan.customFields?.forEach((field) => allFieldNames.add(field.fieldName));
    }

    for (const fieldName of Array.from(allFieldNames)) {
      const allEmpty = planList.every((plan) =>
        plan.customFields?.some(
          (f) => f.fieldName === fieldName && isInvalidValue(f.fieldValue)
        )
      );
      if (allEmpty && !notApplicableTitles.includes(fieldName)) {
        notApplicableTitles.push(fieldName);
      }
    }
  }
  if (planType === MDVTypes.VISION) {
    const serviceCodeToTitleMap = new Map<number, string>();

    const targetServices: Record<
      number,
      { title: string; type: 'FREQUENCY' | 'SERVICES' }
    > = {
      12: { title: 'Exam Copay', type: 'SERVICES' },
      71: { title: 'Frames', type: 'SERVICES' },
      72: { title: 'Eyeglass Lenses Single', type: 'SERVICES' },
      73: { title: 'Eyeglass Lenses Bifocal', type: 'SERVICES' },
      74: { title: 'Eyeglass Lenses Trifocal', type: 'SERVICES' },
      75: { title: 'Elective Contacts', type: 'SERVICES' },
      76: { title: 'Medically Necessary Contacts', type: 'SERVICES' },
      102: { title: 'Eye Exam Frequency', type: 'FREQUENCY' },
      103: { title: 'Lenses Frequency', type: 'FREQUENCY' },
      104: { title: 'Frames Frequency', type: 'FREQUENCY' },
      109: { title: 'Materials Copay', type: 'SERVICES' },
      110: { title: 'Eyeglass Lenses Lenticular', type: 'SERVICES' },
    };

    // Collect service codes from plans
    planList.forEach((plan) => {
      (plan.customServices || []).forEach((service) => {
        const benefitCode = service.benefitCode;

        if (
          benefitCode?.code !== undefined &&
          benefitCode?.benefitKind === planType.toUpperCase() &&
          benefitCode.shortName
        ) {
          const title = benefitCode.shortName;
          serviceCodeToTitleMap.set(benefitCode.code, title);
        }
      });
    });

    // Merge with defaults
    const unifiedMap = new Map<number, string>();
    serviceCodeToTitleMap.forEach((value, key) => unifiedMap.set(key, value));
    Object.entries(targetServices).forEach(([key, { title }]) => {
      const numKey = Number(key);
      if (!unifiedMap.has(numKey)) {
        unifiedMap.set(numKey, title);
      }
    });

    Array.from(unifiedMap).forEach(([code, title]) => {
      const serviceMeta = targetServices[code];
      const prefix =
        serviceMeta?.type === 'FREQUENCY'
          ? 'VISION_FREQUENCY_'
          : 'VISION_SERVICES_';
      const fullTitle = prefix + title;

      if (networkType === 'OUT' && serviceMeta?.type === 'FREQUENCY') {
        // Always push frequency values for OUT network - UAT-519 , even vision frequency has OON network extracted from LLM, we do not show in DBG.
        if (!notApplicableTitles.includes(fullTitle)) {
          notApplicableTitles.push(fullTitle);
        }
        return;
      }

      // Default check (only push if not covered)
      const allPlansNotCovered = planList.every((plan) => {
        const allServices = [...(plan.customServices ?? [])];
        const matchingService = allServices.find(
          (service) => service.benefitCode?.code === code
        );
        if (!matchingService) return true;

        const infoField = getServiceValue(matchingService, networkType);
        return !infoField || infoField.trim() === '';
      });

      if (allPlansNotCovered && !notApplicableTitles.includes(fullTitle)) {
        notApplicableTitles.push(fullTitle);
      }
    });

    // Custom fields check remains the same
    const allFieldNames = new Set<string>();
    for (const plan of planList) {
      plan.customFields?.forEach((field) => allFieldNames.add(field.fieldName));
    }

    for (const fieldName of Array.from(allFieldNames)) {
      const allEmpty = planList.every((plan) =>
        plan.customFields?.some(
          (f) => f.fieldName === fieldName && isInvalidValue(f.fieldValue)
        )
      );
      if (allEmpty && !notApplicableTitles.includes(fieldName)) {
        notApplicableTitles.push(fieldName);
      }
    }
  }

  return notApplicableTitles;
};

export const hideHeader = (
  planType: MDVTypes,
  notApplicableTitles: string[],
  fieldValue: string,
  networkType: string
): boolean => {
  if (planType === MDVTypes.MEDICAL) {
    if (fieldValue === 'SERVICES') {
      const requiredMedicalServices = [
        'MEDICAL_SERVICES_Office Visit',
        'MEDICAL_SERVICES_Specialist Visit',
        'MEDICAL_SERVICES_Urgent Care',
        'MEDICAL_SERVICES_Emergency Room',
        'MEDICAL_SERVICES_Chiropractic Care',
        'MEDICAL_SERVICES_Complex Imaging',
        'MEDICAL_SERVICES_Acupuncture Care',
        'MEDICAL_SERVICES_Diagnostic Lab/ X-Ray',
        'MEDICAL_SERVICES_Hospital Inpatient',
        'MEDICAL_SERVICES_Hospital Outpatient',
        'MEDICAL_SERVICES_Preventive Care',
        'MEDICAL_SERVICES_Coinsurance',
        'MEDICAL_SERVICES_Virtual Care',
      ];
      return requiredMedicalServices.every((title) =>
        notApplicableTitles.includes(title)
      );
    }

    if (fieldValue === 'DEDUCTIBLES & OOP MAXIMUM') {
      const requiredMedicalDeductibles = [
        'MEDICAL_DEDUCTIBLE_Individual Deductible',
        'MEDICAL_DEDUCTIBLE_Family Deductible',
        'MEDICAL_DEDUCTIBLE_Deductible (Ind within Fam)',
        'MEDICAL_DEDUCTIBLE_Individual Out-of-Pocket Max',
        'MEDICAL_DEDUCTIBLE_Family Out-of-Pocket Max',
        'MEDICAL_DEDUCTIBLE_Out-of-Pocket Max (Ind within Fam)',
      ];
      return requiredMedicalDeductibles.every((title) =>
        notApplicableTitles.includes(title)
      );
    }

    if (fieldValue === 'RX') {
      const requiredRxTitles = [
        'MEDICAL_RX_Individual Deductible',
        'MEDICAL_RX_Family Deductible',
        'MEDICAL_RX_Individual Out-of-Pocket Max',
        'MEDICAL_RX_Family Out-of-Pocket Max',
        ...Array.from(
          { length: 8 },
          (_, i) => `MEDICAL_RX_Retail Tier ${i + 1}`
        ),
        ...Array.from(
          { length: 8 },
          (_, i) => `MEDICAL_RX_Mail Order Tier ${i + 1}`
        ),
      ];

      return requiredRxTitles.every((title) =>
        notApplicableTitles.includes(title)
      );
    }
  }
  if (planType === MDVTypes.DENTAL) {
    if (fieldValue === 'SERVICES') {
      const requiredDentalServices = [
        'DENTAL_SERVICES_Preventive Care',
        'DENTAL_SERVICES_Basic Care',
        'DENTAL_SERVICES_Major Care',
        'DENTAL_SERVICES_Endodontic Care',
        'DENTAL_SERVICES_Periodontic Care',
        'DENTAL_SERVICES_Waiting Period',
        'DENTAL_SERVICES_Implants',
      ];
      if (networkType === 'OUT') {
        requiredDentalServices.push(
          'DENTAL_SERVICES_Out-of-Network Reimbursement'
        );
      }
      return requiredDentalServices.every((title) =>
        notApplicableTitles.includes(title)
      );
    }

    if (fieldValue === 'DEDUCTIBLES & BENEFIT MAXIMUMS') {
      const requiredDentalDeductibles = [
        'DENTAL_DEDUCTIBLE_Annual Maximum (Per Person)',
        'DENTAL_DEDUCTIBLE_Individual Deductible',
        'DENTAL_DEDUCTIBLE_Family Deductible',
        'DENTAL_DEDUCTIBLE_Orthodontia Maximum',
      ];
      return requiredDentalDeductibles.every((title) =>
        notApplicableTitles.includes(title)
      );
    }
  }

  if (planType === MDVTypes.VISION) {
    if (fieldValue === 'FREQUENCIES') {
      if (networkType === 'OUT') {
        return true; // always hide when network is OUT UAT-519
      }
      const requiredVisionFrequencies = [
        'VISION_FREQUENCY_Eye Exam Frequency',
        'VISION_FREQUENCY_Lenses Frequency',
        'VISION_FREQUENCY_Frames Frequency',
      ];
      return requiredVisionFrequencies.every((title) =>
        notApplicableTitles.includes(title)
      );
    }

    if (fieldValue === 'SERVICES') {
      const requiredVisionServices = [
        'VISION_SERVICES_Exam Copay',
        'VISION_SERVICES_Materials Copay',
        'VISION_SERVICES_Eyeglass Lenses Single',
        'VISION_SERVICES_Eyeglass Lenses Bifocal',
        'VISION_SERVICES_Eyeglass Lenses Trifocal',
        'VISION_SERVICES_Eyeglass Lenses Lenticular',
        'VISION_SERVICES_Frames',
        'VISION_SERVICES_Elective Contacts',
        'VISION_SERVICES_Medically Necessary Contacts',
      ];
      return requiredVisionServices.every((title) =>
        notApplicableTitles.includes(title)
      );
    }
  }

  return false;
};

export const getServiceValue = (
  matchingService: { serviceValue?: any } | undefined,
  networkType: 'IN' | 'OUT'
): string => {
  if (!matchingService?.serviceValue) {
    return '';
  }

  const networkValues =
    networkType === 'IN'
      ? matchingService.serviceValue.inNetwork
      : matchingService.serviceValue.outOfNetwork;

  if (!networkValues) {
    return '';
  }

  // Check in order of priority
  return (
    networkValues.coinsurance?.toString()?.trim() ||
    networkValues.copay?.toString()?.trim() ||
    networkValues.info?.toString()?.trim()
  );
};

/**
 * Enum for maintaining all file-types and corresponding mime-types used the system.
 * Please extend this object if trying to use a file-type not present in the list.
 */
export const MIME_TYPES = {
  PDF: 'application/pdf',
  CSV: 'text/csv',
  XLS: 'application/vnd.ms-excel',
  DOC: 'application/msword',
  PPT: 'application/vnd.ms-powerpoint',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  POTX: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  PPSX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  JPG: 'image/jpg',
  JPEG: 'image/jpeg',
  PNG: 'image/png',
  ZIP: 'application/zip',
} as const;

export const swapElements = (arr: any[], index1: number, index2: number) => {
  const newArray = [...arr];
  [newArray[index1], newArray[index2]] = [newArray[index2], newArray[index1]];
  return newArray;
};

export const extractBaseTitle = (title: { fieldValue: string }): string => {
  const rawValue = title.fieldValue.split('_')[2] || '';
  const keywordsToTrim = [
    'Frames Frequency',
    'Lenses Frequency',
    'Eye Exam Frequency',
  ];

  const matched = keywordsToTrim.find((keyword) => keyword === rawValue);

  return matched ? matched.replace(' Frequency', '') : rawValue;
};

export const isInvalidDisplayValue = (val: any) => {
  const invalidValues = [
    defaultValue,
    DOUBLE_DASH,
    TRIPLE_DASH,
    NO_DATA,
    EMPTY_VALUE,
  ];
  if (val === undefined || val === null) return true;
  const valStr = String(val).trim();
  const firstValue = valStr.split(' ')[0].toUpperCase();

  return valStr.startsWith('No data') || invalidValues.includes(firstValue);
};
